import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  ADMIN_NAVIGATION_ITEMS,
  isAdminNavigationItemVisible,
  type AdminNavigationItem,
} from './navigationItems';

const MAXIMUM_PRIMARY_ITEMS = 5;

export function AdminMobileNav() {
  const { t } = useTranslation(['admin', 'common']);
  const { platformPermissions } = useAuth();
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);

  const visibleNavigationItems = ADMIN_NAVIGATION_ITEMS.filter(
    function keepPermittedItem(item: AdminNavigationItem) {
      return isAdminNavigationItemVisible(item, platformPermissions);
    }
  );

  const requiresMoreSheet = visibleNavigationItems.length > MAXIMUM_PRIMARY_ITEMS;
  const primaryItemCount = requiresMoreSheet
    ? MAXIMUM_PRIMARY_ITEMS - 1
    : visibleNavigationItems.length;
  const primaryItems = visibleNavigationItems.slice(0, primaryItemCount);
  const overflowItems = visibleNavigationItems.slice(primaryItemCount);

  return (
    <>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-zoowhite border-t border-zooborder flex">
        {primaryItems.map(function renderMobileNavItem(item) {
          return <AdminMobileNavLink key={item.id} item={item} label={t(item.labelKey)} />;
        })}
        {requiresMoreSheet === true ? (
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={isMoreSheetOpen}
            onClick={function openMoreSheet() {
              setIsMoreSheetOpen(true);
            }}
            className={cn(
              'flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-medium transition-colors',
              isMoreSheetOpen === true ? 'text-zooprimary' : 'text-zoomuted'
            )}
          >
            <Icon name="MoreHorizontal" className="text-lg" />
            <span className="w-full truncate text-center">{t('common:navigation.more')}</span>
          </button>
        ) : null}
      </nav>

      {isMoreSheetOpen === true ? (
        <div className="md:hidden fixed inset-0 z-40 flex flex-col justify-end">
          <button
            type="button"
            aria-label={t('common:actions.close')}
            onClick={function closeMoreSheet() {
              setIsMoreSheetOpen(false);
            }}
            className="absolute inset-0 bg-zooindigo bg-opacity-50 backdrop-blur-sm"
          />
          <div
            role="menu"
            className="relative bg-zoowhite border-t border-zooborder rounded-t-3xl p-4 pb-6"
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-zooborder" />
            <ul className="divide-y divide-zooborder">
              {overflowItems.map(function renderOverflowItem(item) {
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.route}
                      role="menuitem"
                      onClick={function closeOnSelect() {
                        setIsMoreSheetOpen(false);
                      }}
                      className={function resolveClassName({ isActive }) {
                        return cn(
                          'flex items-center gap-3 px-2 py-3 text-sm font-medium transition-colors',
                          isActive === true ? 'text-zooprimary' : 'text-zooindigo'
                        );
                      }}
                    >
                      <Icon name={item.iconName} className="text-lg shrink-0" />
                      <span className="min-w-0 truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
}

interface AdminMobileNavLinkProps {
  item: AdminNavigationItem;
  label: string;
}

function AdminMobileNavLink({ item, label }: AdminMobileNavLinkProps) {
  return (
    <NavLink
      to={item.route}
      end={item.route === APPLICATION_ROUTES.adminDashboard}
      className={function resolveClassName({ isActive }) {
        return cn(
          'flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-medium transition-colors',
          isActive === true ? 'text-zooprimary' : 'text-zoomuted'
        );
      }}
    >
      <Icon name={item.iconName} className="text-lg" />
      <span className="w-full truncate text-center">{label}</span>
    </NavLink>
  );
}
